/**
 * Personal Details Page - First onboarding page (Step 1 of 4).
 * Feature 016: Conversational onboarding flow collecting name, DOB, height, weight.
 *
 * User Story 2: "Let's get to know you 👋"
 * Collects: first_name, last_name, date_of_birth, height, weight (optional)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useOnboarding } from '../../hooks/useOnboarding';
import ProgressIndicator from '../../components/onboarding/ProgressIndicator';

const PersonalDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { savePage, isSaving } = useOnboarding();

  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('1976-01-01');
  const [height, setHeight] = useState('');
  const [weight, setWeight] = useState('');
  const [unitSystem, setUnitSystem] = useState<'metric' | 'imperial'>('metric');

  // Validation state
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');

  // Unit conversion constants
  const KG_TO_LBS = 2.20462262;
  const CM_TO_INCHES = 0.393701;

  // Pre-fill existing data if user has already started onboarding
  useEffect(() => {
    if (user?.profile) {
      const { first_name, last_name, date_of_birth, physical_profile } = user.profile;

      if (first_name) setFirstName(first_name);
      if (last_name) setLastName(last_name);
      if (date_of_birth) setDateOfBirth(date_of_birth);

      if (physical_profile) {
        if (physical_profile.height) {
          setHeight(physical_profile.height.value.toString());
        }
        if (physical_profile.weight) {
          setWeight(physical_profile.weight.value.toString());
        }
        if (physical_profile.preferred_unit_system === 'metric' || physical_profile.preferred_unit_system === 'imperial') {
          setUnitSystem(physical_profile.preferred_unit_system);
        }
      }
    }
  }, [user]);

  // Validate age 18+
  const validateAge = (dob: string): boolean => {
    const birthDate = new Date(dob);
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      return age - 1 >= 18;
    }

    return age >= 18;
  };

  // Validate height and weight ranges (metric)
  const validatePhysicalMetrics = (h: string, w: string): Record<string, string> => {
    const validationErrors: Record<string, string> = {};

    if (h && w) {
      const heightValue = parseFloat(h);
      const weightValue = parseFloat(w);

      // Validate height (100-250 cm or 39-98 inches)
      if (unitSystem === 'metric') {
        if (heightValue < 100 || heightValue > 250) {
          validationErrors.height = 'Height must be between 100-250 cm';
        }
      } else {
        if (heightValue < 39 || heightValue > 98) {
          validationErrors.height = 'Height must be between 39-98 inches';
        }
      }

      // Validate weight (30-300 kg or 66-661 lbs)
      if (unitSystem === 'metric') {
        if (weightValue < 30 || weightValue > 300) {
          validationErrors.weight = 'Weight must be between 30-300 kg';
        }
      } else {
        if (weightValue < 66 || weightValue > 661) {
          validationErrors.weight = 'Weight must be between 66-661 lbs';
        }
      }
    }

    // Paired validation: if one is provided, both must be provided
    if ((h && !w) || (!h && w)) {
      validationErrors.paired = 'Please provide both height and weight, or leave both empty';
    }

    return validationErrors;
  };

  // Handle unit toggle with conversion
  const handleUnitToggle = (newSystem: 'metric' | 'imperial') => {
    if (newSystem === unitSystem) return;

    // Convert height
    if (height) {
      const heightValue = parseFloat(height);
      if (!isNaN(heightValue)) {
        if (newSystem === 'imperial') {
          setHeight((heightValue * CM_TO_INCHES).toFixed(1));
        } else {
          setHeight((heightValue / CM_TO_INCHES).toFixed(1));
        }
      }
    }

    // Convert weight
    if (weight) {
      const weightValue = parseFloat(weight);
      if (!isNaN(weightValue)) {
        if (newSystem === 'imperial') {
          setWeight((weightValue * KG_TO_LBS).toFixed(1));
        } else {
          setWeight((weightValue / KG_TO_LBS).toFixed(1));
        }
      }
    }

    setUnitSystem(newSystem);
  };

  // Handle form submission
  const handleNext = async () => {
    setErrors({});

    // Validate required fields
    const validationErrors: Record<string, string> = {};

    if (!firstName.trim()) {
      validationErrors.firstName = 'First name is required';
    }

    if (!lastName.trim()) {
      validationErrors.lastName = 'Last name is required';
    }

    if (!dateOfBirth) {
      validationErrors.dateOfBirth = 'Date of birth is required';
    } else if (!validateAge(dateOfBirth)) {
      validationErrors.dateOfBirth = 'You must be at least 18 years old';
    }

    // Validate optional physical metrics
    const physicalErrors = validatePhysicalMetrics(height, weight);
    Object.assign(validationErrors, physicalErrors);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // Prepare data for API
    const pageData: any = {
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      date_of_birth: dateOfBirth,
    };

    // Include physical profile only if both height and weight provided
    if (height && weight) {
      // Convert to metric for backend storage
      let heightCm = parseFloat(height);
      let weightKg = parseFloat(weight);

      if (unitSystem === 'imperial') {
        heightCm = heightCm / CM_TO_INCHES;
        weightKg = weightKg / KG_TO_LBS;
      }

      pageData.physical_profile = {
        height: { value: Math.round(heightCm * 10) / 10, unit: 'cm' },
        weight: { value: Math.round(weightKg * 10) / 10, unit: 'kg' },
        preferred_unit_system: unitSystem,
      };
    }

    try {
      // T030: Save via saveOnboardingPage API
      await savePage.mutateAsync({ page: 'personal_details', data: pageData });

      // T031: Success toast
      setToastType('success');
      setToastMessage('Personal details saved successfully!');
      setShowToast(true);

      // T032: Navigate to next page
      setTimeout(() => {
        navigate('/onboarding/goals');
      }, 1000);
    } catch (err: any) {
      // T031: Error handling
      setToastType('error');
      setToastMessage(err.message || 'Failed to save personal details. Please try again.');
      setShowToast(true);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-100 to-primary-200 px-4 py-12">
      <div className="max-w-2xl w-full animate-fade-in">
        {/* T026: Progress Indicator - Step 1 of 4, 25% */}
        <ProgressIndicator currentStep={1} totalSteps={4} />

        <div className="card shadow-elegant mt-8">
          {/* T025: Page header with emoticon */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-serif font-bold text-primary-800 mb-3">
              Let's get to know you 👋
            </h1>
            <p className="text-base text-neutral-600">
              Just a few details to personalize your wellness journey
            </p>
          </div>

          {/* Error Toast with aria-live for screen readers (T086) */}
          {showToast && (
            <div
              role="alert"
              aria-live="assertive"
              aria-atomic="true"
              className={`mb-6 p-4 border-l-4 rounded-r-lg animate-slide-down ${
                toastType === 'success'
                  ? 'bg-green-50 border-green-400'
                  : 'bg-red-50 border-red-400'
              }`}
            >
              <p
                className={`text-sm font-medium ${
                  toastType === 'success' ? 'text-green-800' : 'text-red-800'
                }`}
              >
                {toastMessage}
              </p>
            </div>
          )}

          {/* T027: First name and last name inputs */}
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-neutral-700 mb-2">
                  First Name
                </label>
                <input
                  id="firstName"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className={`input input-large ${errors.firstName ? 'border-red-400' : ''}`}
                  placeholder="Your first name"
                  required
                />
                {errors.firstName && (
                  <p className="text-sm text-red-600 mt-1">{errors.firstName}</p>
                )}
              </div>

              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-neutral-700 mb-2">
                  Last Name
                </label>
                <input
                  id="lastName"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className={`input input-large ${errors.lastName ? 'border-red-400' : ''}`}
                  placeholder="Your last name"
                  required
                />
                {errors.lastName && (
                  <p className="text-sm text-red-600 mt-1">{errors.lastName}</p>
                )}
              </div>
            </div>

            {/* T028: Date of birth with age 18+ validation */}
            <div>
              <label htmlFor="dateOfBirth" className="block text-sm font-medium text-neutral-700 mb-2">
                Date of Birth (must be 18+)
              </label>
              <input
                id="dateOfBirth"
                type="date"
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
                className={`input input-large ${errors.dateOfBirth ? 'border-red-400' : ''}`}
                required
              />
              {errors.dateOfBirth && (
                <p className="text-sm text-red-600 mt-1">{errors.dateOfBirth}</p>
              )}
            </div>

            {/* T029: Optional height and weight with unit toggle */}
            <div className="border-t border-neutral-200 pt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-neutral-800">
                  Body Metrics (Optional)
                </h3>
                {/* Unit toggle */}
                <div className="flex bg-neutral-100 rounded-full p-1">
                  <button
                    type="button"
                    onClick={() => handleUnitToggle('metric')}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                      unitSystem === 'metric'
                        ? 'bg-white text-primary-700 shadow-md'
                        : 'text-neutral-600 hover:text-neutral-800'
                    }`}
                  >
                    Metric
                  </button>
                  <button
                    type="button"
                    onClick={() => handleUnitToggle('imperial')}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                      unitSystem === 'imperial'
                        ? 'bg-white text-primary-700 shadow-md'
                        : 'text-neutral-600 hover:text-neutral-800'
                    }`}
                  >
                    Imperial
                  </button>
                </div>
              </div>

              <p className="text-sm text-neutral-600 mb-4">
                Help us understand your starting point for goal tracking
              </p>

              {errors.paired && (
                <p className="text-sm text-red-600 mb-4">{errors.paired}</p>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="height" className="block text-sm font-medium text-neutral-700 mb-2">
                    Height ({unitSystem === 'metric' ? 'cm' : 'inches'})
                  </label>
                  <input
                    id="height"
                    type="number"
                    step="0.1"
                    value={height}
                    onChange={(e) => setHeight(e.target.value)}
                    className={`input input-large ${errors.height ? 'border-red-400' : ''}`}
                    placeholder={unitSystem === 'metric' ? 'e.g., 165' : 'e.g., 65'}
                  />
                  {errors.height && (
                    <p className="text-sm text-red-600 mt-1">{errors.height}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="weight" className="block text-sm font-medium text-neutral-700 mb-2">
                    Weight ({unitSystem === 'metric' ? 'kg' : 'lbs'})
                  </label>
                  <input
                    id="weight"
                    type="number"
                    step="0.1"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    className={`input input-large ${errors.weight ? 'border-red-400' : ''}`}
                    placeholder={unitSystem === 'metric' ? 'e.g., 65' : 'e.g., 143'}
                  />
                  {errors.weight && (
                    <p className="text-sm text-red-600 mt-1">{errors.weight}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Submit button */}
            <button
              type="button"
              onClick={handleNext}
              disabled={isSaving}
              className="btn btn-primary w-full text-lg py-4 mt-8 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : 'Next Step →'}
            </button>
          </div>
        </div>

        {/* Footer note */}
        <p className="mt-6 text-xs text-center text-neutral-600">
          Your information is private and secure. We'll never share it without your permission.
        </p>
      </div>
    </div>
  );
};

export default PersonalDetailsPage;
